"use client";

import Link from "next/link";
import Image from "next/image";
import { useMemo, useState } from "react";
import { ShoppingCart, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import type { Product } from "@/lib/products";
import { useCart } from "@/context/CartContext";

type Props = {
  slug: string;
  categoryName: string;
  subcategories: string[];
  products: Product[];
};

export function CategoryCatalog({ slug, categoryName, subcategories, products }: Props) {
  const [selectedSubCategory, setSelectedSubCategory] = useState("All");
  const { addToCart, items } = useCart();

  const filtered = useMemo(
    () =>
      selectedSubCategory === "All"
        ? products
        : products.filter((m) => m.subcategory === selectedSubCategory),
    [selectedSubCategory, products],
  );

  return (
    <>
      <section className="border-b border-border bg-background sticky top-[68px] z-30">
        <div className="container mx-auto px-4 py-4 flex gap-2 overflow-x-auto">
          {["All", ...subcategories].map((sub) => (
            <button
              key={sub}
              type="button"
              onClick={() => setSelectedSubCategory(sub)}
              className={`px-4 py-2 rounded-sm text-xs font-bold uppercase tracking-wider whitespace-nowrap border transition ${
                selectedSubCategory === sub
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card text-foreground border-border hover:border-primary"
              }`}
            >
              {sub}
            </button>
          ))}
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="container mx-auto px-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {filtered.map((product) => {
              const isInCart = items.some((item) => item.id === product.id);

              return (
              <div
                key={product.id}
                className="group flex flex-col bg-card border border-border rounded-md overflow-hidden shadow-sm hover:shadow-lg hover:border-primary transition-all duration-300 hover:-translate-y-1"
              >
                <Link href={`/products/${slug}/${product.id}`} className="block relative overflow-hidden">
                  <Image
                    src={product.image?.url || ""}
                    alt={product.name}
                    width={640}
                    height={416}
                    sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
                    className="w-full h-52 object-contain transition-transform duration-500 group-hover:scale-105"
                  />
                </Link>
                <div className="flex-1 flex flex-col p-5 min-w-0">
                  <Link href={`/products/${slug}/${product.id}`}>
                    <h3 className="font-display text-base text-foreground uppercase leading-tight break-words">{product.name}</h3>
                  </Link>
                  <p className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">{product.subcategory}</p>
                  <ul className="mt-4 space-y-1.5 border-t border-border pt-3">
                    {Object.values(product.specs || {})[0]?.slice(0, 3).map((spec) => (
                      <li key={spec.l} className="flex justify-between gap-3 text-xs">
                        <span className="text-muted-foreground">{spec.l}</span>
                        <span className="text-foreground font-semibold text-right">{spec.v}</span>
                      </li>
                    )) ?? null}
                  </ul>
                  <div className="mt-auto grid grid-cols-1 sm:grid-cols-2 gap-2 pt-4">
                    <button
                      type="button"
                      disabled={isInCart}
                      onClick={() => {
                        if (isInCart) return;
                        addToCart({
                          id: product.id,
                          name: product.name,
                          image: product.image?.url || "",
                          subcategory: product.subcategory,
                          category: categoryName,
                          categorySlug: product.categorySlug,
                          model: product.model,
                        });
                        toast.success(`${product.name} added to cart`);
                      }}
                      className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-sm text-xs font-bold uppercase tracking-wider transition ${
                        isInCart
                          ? "bg-muted text-muted-foreground cursor-not-allowed"
                          : "bg-primary text-primary-foreground hover:opacity-90"
                      }`}
                    >
                      {isInCart ? (
                        <>
                          <Check className="w-3.5 h-3.5" /> Added
                        </>
                      ) : (
                        <>
                          <ShoppingCart className="w-3.5 h-3.5" /> Add
                        </>
                      )}
                    </button>
                    <Link
                      href={`/products/${slug}/${product.id}`}
                      className="flex items-center justify-center gap-1.5 border-2 border-foreground text-foreground px-3 py-2.5 rounded-sm text-xs font-bold uppercase tracking-wider hover:bg-foreground hover:text-background transition"
                    >
                      Details <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-16 text-muted-foreground">No published models are available in this category yet.</div>
          )}
        </div>
      </section>
    </>
  );
}
